import {
  Box,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  Wrap,
} from "@chakra-ui/react";
import React from "react";
import { HiMiniPlay } from "react-icons/hi2";

import { type SignalEvidence } from "./types";
import { formatDuration, formatTimestamp, getSeekOffset } from "./utils";

const formatMinutesSeconds = (seconds: number): string =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    seconds % 60
  ).padStart(2, "0")}`;

type SeekableTagProps = {
  children: React.ReactNode;
  seekOffset?: number;
  onSeek?(t: number): void;
};

const SeekableTag: React.FC<SeekableTagProps> = ({
  children,
  seekOffset,
  onSeek,
}) => {
  const isClickable = seekOffset != null && onSeek != null;
  const tag = (
    <Tag
      size="sm"
      variant="outline"
      colorScheme="gray"
      bg="white"
      cursor={isClickable ? "pointer" : undefined}
      onClick={isClickable ? () => onSeek(seekOffset) : undefined}
      _hover={
        isClickable
          ? { bg: "white", color: "blue.600", borderColor: "blue.600" }
          : undefined
      }
    >
      <TagLeftIcon as={HiMiniPlay} boxSize="3" />
      <TagLabel>{children}</TagLabel>
    </Tag>
  );

  if (!isClickable) return tag;
  return (
    <Tooltip label="Play" placement="top" hasArrow>
      {tag}
    </Tooltip>
  );
};

type EvidenceDetailsProps = {
  signalEvidence: SignalEvidence;
  interviewStartTime?: string | null;
  interviewDuration?: number | null;
  onSeek?(t: number): void;
};

export const EvidenceDetails: React.FC<EvidenceDetailsProps> = ({
  signalEvidence,
  interviewStartTime,
  interviewDuration,
  onSeek,
}) => {
  switch (signalEvidence.signalType) {
    case "TAB_SWITCHING": {
      const { evidence } = signalEvidence;
      if (!evidence) {
        return <Text>No additional details available.</Text>;
      }
      const { totalCount, events } = evidence;

      const durationLabel =
        interviewDuration != null
          ? `${Math.round(interviewDuration / 60)}-min`
          : "";

      return (
        <Box>
          {totalCount != null && (
            <Text mb="2">
              {totalCount} tab switch{totalCount !== 1 ? "es" : ""} detected
              {durationLabel ? ` during the ${durationLabel} interview` : ""}
            </Text>
          )}
          {events && events.length > 0 && (
            <Wrap spacing="2">
              {events.map((eventPoint) => {
                const offset = getSeekOffset(
                  eventPoint.start,
                  interviewStartTime
                );
                return (
                  <SeekableTag
                    key={`${eventPoint.start}-${eventPoint.end}`}
                    seekOffset={offset}
                    onSeek={onSeek}
                  >
                    {formatTimestamp(eventPoint.start, interviewStartTime)}
                  </SeekableTag>
                );
              })}
            </Wrap>
          )}
        </Box>
      );
    }

    case "WINDOW_BLUR": {
      const { evidence } = signalEvidence;
      if (!evidence) {
        return <Text>No additional details available.</Text>;
      }
      const { totalDurationMs, events } = evidence;
      return (
        <Box>
          {totalDurationMs != null && (
            <Text mb="2">
              Window was out of focus for {formatDuration(totalDurationMs)}{" "}
              total
            </Text>
          )}
          {events && events.length > 0 && (
            <Wrap spacing="2">
              {events.map((eventPoint) => {
                const offset = getSeekOffset(
                  eventPoint.start,
                  interviewStartTime
                );
                return (
                  <SeekableTag
                    key={`${eventPoint.start}-${eventPoint.end}`}
                    seekOffset={offset}
                    onSeek={onSeek}
                  >
                    {formatTimestamp(eventPoint.start, interviewStartTime)}{" "}
                    &ndash;{" "}
                    {formatTimestamp(eventPoint.end, interviewStartTime)} (
                    {formatDuration(eventPoint.durationMs)})
                  </SeekableTag>
                );
              })}
            </Wrap>
          )}
        </Box>
      );
    }

    case "IP_LOCATION_MISMATCH": {
      const { evidence } = signalEvidence;
      if (!evidence) {
        return <Text>No additional details available.</Text>;
      }
      const { reason, ipLocation, resumeLocation } = evidence;
      return (
        <Box>
          <Text>
            {resumeLocation && `Resume: ${resumeLocation}. `}
            {ipLocation && `IP: ${ipLocation}`}
            {!resumeLocation && !ipLocation && reason}
          </Text>
        </Box>
      );
    }

    case "LLM_ANSWER_SIMILARITY": {
      const { evidence } = signalEvidence;
      if (!evidence) {
        return <Text>No additional details available.</Text>;
      }
      const {
        flaggedAnswerCount: flaggedCount,
        totalAnswerCount: totalCount,
        questions,
      } = evidence;
      if (flaggedCount == null) {
        return <Text>No additional details available.</Text>;
      }

      const flaggedQuestions = questions?.filter((q) => q.likelyAiGenerated);

      return (
        <Box>
          <Text mb={flaggedQuestions?.length ? "2" : undefined}>
            {flaggedCount} answer{flaggedCount !== 1 ? "s" : ""} flagged as
            similar to AI-generated responses
            {totalCount != null ? ` out of ${totalCount} total` : ""}
          </Text>
          {flaggedQuestions && flaggedQuestions.length > 0 && (
            <Wrap spacing="2">
              {flaggedQuestions.map((q) => {
                const seconds = q.citationSeconds ?? -1;
                const hasCitation = seconds >= 0;
                const questionPrefix = q.questionNumber
                  ? `Q${q.questionNumber}`
                  : null;
                const topicSuffix =
                  q.topicLabel || q.questionText?.slice(0, 20);
                const label = [questionPrefix, topicSuffix]
                  .filter(Boolean)
                  .join(": ");
                const timestamp = hasCitation
                  ? formatMinutesSeconds(seconds)
                  : null;

                return (
                  <SeekableTag
                    key={q.questionId || q.questionText}
                    seekOffset={hasCitation ? seconds : undefined}
                    onSeek={onSeek}
                  >
                    {timestamp ? `${timestamp}  ` : ""}
                    {label}
                  </SeekableTag>
                );
              })}
            </Wrap>
          )}
        </Box>
      );
    }

    case "SCRIPTED_RESPONSES": {
      const { evidence } = signalEvidence;
      if (!evidence) {
        return <Text>No additional details available.</Text>;
      }
      const {
        userVisibleSummary,
        naturalBaselineCitations,
        scriptedCitations,
      } = evidence;

      return (
        <Box>
          {userVisibleSummary && <Text mb="3">{userVisibleSummary}</Text>}
          {naturalBaselineCitations && naturalBaselineCitations.length > 0 && (
            <Box mb="2">
              <Text fontSize="sm" fontWeight="medium" mb="1">
                Natural sounding baseline:
              </Text>
              <Wrap spacing="2">
                {naturalBaselineCitations.map((c) => (
                  <SeekableTag
                    key={`natural-${c.citationSeconds}`}
                    seekOffset={c.citationSeconds}
                    onSeek={onSeek}
                  >
                    {formatMinutesSeconds(c.citationSeconds)}
                  </SeekableTag>
                ))}
              </Wrap>
            </Box>
          )}
          {scriptedCitations && scriptedCitations.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb="1">
                Sounds scripted:
              </Text>
              <Wrap spacing="2">
                {scriptedCitations.map((c) => (
                  <SeekableTag
                    key={`scripted-${c.citationSeconds}`}
                    seekOffset={c.citationSeconds}
                    onSeek={onSeek}
                  >
                    {formatMinutesSeconds(c.citationSeconds)}
                  </SeekableTag>
                ))}
              </Wrap>
            </Box>
          )}
        </Box>
      );
    }

    default:
      return <Text>No additional details available.</Text>;
  }
};
